import sha1 from 'sha1'
import { nanoid } from 'nanoid'
import { emitCustomEvent } from 'react-custom-events'
import {
  formatPriceEstimateRequest,
  formatBookingRequest,
  formatPackageBookingRequest,
  formatEditEstimateRequest,
  formatStatusRequest,
  formatLocationQuery,
  formatServiceAvailabiltyQuery,
  formatPhoneNumber
} from '~/utils/format'
import { getErrorMessage } from './errors'
import i18next from '~/i18n'

export class ApiError extends Error {
  constructor(message, response, status) {
    super(message)
    this.name = this.constructor.name
    this.response = response
    this.status = status
    this.isUnauthorized = status === 401
  }
}

// The request never produced a response (offline, DNS/TLS failure, connection dropped
// mid-flight). For a mutating request this is *ambiguous*: the server may well have
// processed it and only the response was lost, so the caller must not silently retry.
export class NetworkError extends ApiError {
  constructor(message, isUncertain) {
    super(message, { error: 'NETWORK_ERROR', message }, null)
    this.isNetworkError = true
    // false => the request had no lasting effect, safe to retry
    // true  => the server may have created or changed a booking without us knowing,
    //          so the user must reconcile against active bookings before retrying
    this.isUncertain = isUncertain
  }
}

// Endpoints where a lost response leaves booking state unknown. Deliberately not
// "any non-GET": price estimates, status and search are POSTs but read-only, and
// telling someone their booking may have gone through after a failed price lookup
// would be both wrong and alarming.
const changesBookingState = (endPoint, method) => {
  if (method === 'POST') {
    return /^\/v1\/bookings(\/prepare)?(\?|$)/.test(endPoint) ||
      /^\/v1\/bookings\/[^/]+\/confirm-edit(\?|$)/.test(endPoint)
  }
  if (method === 'DELETE') {
    return /^\/v1\/bookings\/[^/?]+(\?|$)/.test(endPoint)
  }
  return false
}

export const hasToken = () => {
  return localStorage.getItem('token') !== null
}

const getOrCreateDeviceId = () => {
  let id = localStorage.getItem('uuid')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('uuid', id)
  }
  return id
}

export const isBadState = error => {
  // TODO: Add more error codes?
  return error?.response?.error === 'DEVICE_ACTIVATION_NOT_FOUND'
}

const pendingRequests = new Map()

async function abortable_api(id, endPoint, body, method = 'GET', extraHeaders = null, returnFull = false) {
  const prevController = pendingRequests.get(id)

  if (prevController) {
    prevController.abort()
  }
  const controller = new AbortController()
  pendingRequests.set(id, controller)

  return api(endPoint, body, method, extraHeaders, returnFull, controller.signal).then(response => {
    pendingRequests.delete(id)
    return response
  })
}

async function api(endPoint, body, method = 'GET', extraHeaders = null, returnFull = false, signal = null) {
  const token = localStorage.getItem('token')

  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': i18next.language,
    'X-Device-Id': getOrCreateDeviceId(),
    ...extraHeaders
  }
  if (token) headers['Authorization'] = token

  const options = { method, headers, body }

  // Make request abortable using AbortController
  if (signal) options.signal = signal

  const isUncertain = changesBookingState(endPoint, method)

  const res = await fetch(`/api${endPoint}`, options)
  .catch((error) => {
    if (error.name === 'AbortError') {
      throw error
    }
    console.error(error)
    // Without this the undefined `res` below threw a TypeError, which surfaced to the
    // user as "Cannot read properties of undefined" and let them retry a booking that
    // may already have been created. Throw a typed error the callers can reason about.
    throw new NetworkError(
      i18next.t(isUncertain ? 'error-network-uncertain' : 'error-network'),
      isUncertain
    )
  })

  if (returnFull && res.status === 200) return res

  // Check for API errors (non-2xx status codes)
  if (res.status < 200 || res.status >= 300) {
    // A 502/504 from the gateway can come back as HTML rather than JSON, so don't
    // let a parse failure escape as a SyntaxError instead of an ApiError.
    const json = await res.json().catch(() => ({}))
    if (json?.error) json.message = getErrorMessage(endPoint, json.error, res.status)
    else if (!json?.message) json.message = getErrorMessage(endPoint, null, res.status)

    if (res.status === 401 && json?.error === 'Unauthorized') {
      emitCustomEvent('UNAUTHORIZED')
      // Throw a special error that won't show error modals
      // The UNAUTHORIZED event handler will show the proper logout modal
      const error = new ApiError('Unauthorized', { error: 'Unauthorized', message: 'Unauthorized' }, 401)
      error.isUnauthorized = true
      throw error
    }

    throw new ApiError(`${json.error} ${json.message}`, json, res.status)
  }

  // For successful responses, check if there's a body to parse
  const contentType = res.headers.get('content-type')
  const contentLength = res.headers.get('content-length')

  // If no content or content-length is 0, return empty object
  if (contentLength === '0' || !contentType || !contentType.includes('application/json')) {
    // Save user token
    if (res.headers.has('Authorization')) {
      localStorage.setItem('token', res.headers.get('Authorization'))
    }
    return {}
  }

  const json = await res.json()

  // Save user token
  if (res.headers.has('Authorization')) {
    localStorage.setItem('token', res.headers.get('Authorization'))
  }

  return json
}


/*
 *
 *  Locations
 *
 */
export async function searchLocation(value, region, city, position) {
  value = value.replace(/[\s,]+/g, ' ')
  let url = `/v1/locations/search?address_string=${encodeURIComponent(value)}`
  if (region !== undefined && region !== null) {
    url += `&region=${encodeURIComponent(region)}`
  }
  if (city !== undefined && city !== null) {
    url += `&city=${encodeURIComponent(city)}`
  }
  if (position !== undefined && position !== null) {
    url += `&reference_latitude=${encodeURIComponent(position.latitude)}`
    url += `&reference_longitude=${encodeURIComponent(position.longitude)}`
  }
  const res = await abortable_api('searchLocation', url)
  return res.result || {}
}

export async function fetchStreetNumbers(streetName, city) {
  const res = await api(`/v1/locations/numbers?street_name=${encodeURIComponent(streetName)}&city=${encodeURIComponent(city)}`)
  return res
}

export async function fetchLocationInfo(location) {
  const res = await api(`/v1/locations/${location.id}`)
  // NOTE: Don't know if this is needed with v3
  // const query = formatLocationQuery(location.address)
  // const res = await api(`/v1/locations/${encodeURIComponent(query)}`)

  // NOTE: Don't know if this is needed with v3
  // if (res?.address?.entrance === '') delete res.address.entrance

  // Add unique id to every location object to use as React key in the search locations component.
  // This allows duplicate addresses, for example if departure and destination is the same but with multiple stops in between.
  if (res?.address?.streetName) {
    res.key = nanoid(10)
  }
  return res
}

export async function searchNearby({ latitude, longitude, accuracy }) {
  // const res = await api(`/v1/locations/nearby?latitude=${latitude}&longitude=${longitude}&accuracy=${Math.ceil(accuracy)}`)
  const res = await api(`/v1/locations/nearby?latitude=${latitude}&longitude=${longitude}`)
  return res.result || {}
}

export async function fetchEta({ latitude, longitude }) {
  // NOTE: the API returns ETA for all vehicle types but taxi is the only accurate one so we disable this for now
  // const res = await api(`/v1/estimates/wait?latitude=${latitude}&longitude=${longitude}`)
  const res = await api(`/v1/estimates/wait?vehicleType=taxi&latitude=${latitude}&longitude=${longitude}`)
  return res
}

export async function fetchServiceAvailability(values, user) {
  const query = formatServiceAvailabiltyQuery(values, user)
  const res = await abortable_api('fetchServiceAvailability', `/v1/service-availability?${query}`)
  return res
}

export async function getRegions() {
  const res = await api(`/v1/regions`)
  return res
}



/*
 *
 *  Price estimate
 *
 */
export async function fetchPriceEstimate(values, freshAvailability = null) {
  const req = formatPriceEstimateRequest(values, freshAvailability)
  const res = await abortable_api('fetchPriceEstimate', '/v1/price/estimate', JSON.stringify(req), 'POST')
  return res
}


/*
 *
 *  OTP activation 
 *
 */
export async function initiateActivation(code, phone) {
  const formatted = code + phone.replace(/^0/, '')
  const req = { phone: formatted, locale: i18next.language, acceptTermsAndConditions: true }
  const res = await api('/v1/activation/initiate', JSON.stringify(req), 'POST')
  return res
}

export async function resendPhoneOtp() { 
  const res = await api('/v1/activation/resend-phone-otp', null, 'POST')
  return res
}

export async function validatePhoneOtp(otp) {
  const req = { otp }
  const res = await api('/v1/activation/validate-phone-otp', JSON.stringify(req), 'POST')
  return res
}

export async function resendEmailOtp() {
  const res = await api('/v1/activation/resend-email-otp', null, 'POST')
  return res
}

export async function validateEmailOtp(otp) {
  const req = { otp }
  const res = await api('/v1/activation/validate-email-otp', JSON.stringify(req), 'POST')
  return res
}

export async function addEmail(email) {
  const req = { email }
  const res = await api('/v1/activation/add-email', JSON.stringify(req), 'PATCH')
  return res
}

export async function addName(firstName, lastName) {
  const req = { firstName, lastName }
  const res = await api('/v1/activation/add-name', JSON.stringify(req), 'PATCH')
  return res
}

export async function completeActivation(req) {
  const res = await api('/v1/activation/complete', JSON.stringify(req), 'POST')
  return res
}

// Changing/verifying the phone number of a logged in user goes through activation
// rather than PATCH /users/{userId}, since it has to be confirmed with an OTP.
// Responds with UPDATE_PHONE_OTP_INITIATED, then validatePhoneOtp() finishes it.
export async function updatePhoneNumber(phone) {
  const req = { phone: formatPhoneNumber(phone) }
  const res = await api('/v1/activation/update-phone-number', JSON.stringify(req), 'PATCH')
  return res
}


/*
 *
 *  Social login
 *
 */
export async function initiateGoogleSignIn() {
  const req = { authType: 'GOOGLE_SIGN_IN', locale: i18next.language, acceptTermsAndConditions: true }
  const res = await api('/v1/activation/initiate', JSON.stringify(req), 'POST')
  return res
}

export async function initiateAppleSignIn() {
  const req = { authType: 'APPLE_ID', locale: i18next.language, acceptTermsAndConditions: true }
  const res = await api('/v1/activation/initiate', JSON.stringify(req), 'POST')
  return res
}

export async function validateOidcToken(idToken) {
  const req = { payload: idToken }
  const res = await api('/v1/activation/validate-oidc-token', JSON.stringify(req), 'POST')
  return res
}

export async function addPhoneNumberSocial(dialCode, phone) {
  const formatted = dialCode + phone.replace(/^0/, '')
  const req = { phone: formatted }
  const res = await api('/v1/activation/add-phone-number', JSON.stringify(req), 'PATCH')
  return res
}


/*
 *
 *  User
 *
 */
export async function getUser(userId) {
  const res = await api(`/v1/users/${userId}`)
  return res
}

export async function updateUser(userId, { firstName, lastName, email, marketingOptIn }) {
  const req = { firstName, lastName, email, marketingOptIn }
  const res = await api(`/v1/users/${userId}`, JSON.stringify(req), 'PATCH')
  return res
}

export async function updateMarketingConsent(userId, consent) {
  const res = await api(`/v1/users/${userId}`, JSON.stringify({ marketingOptIn: consent }), 'PATCH')
  return res
}

export async function deleteUser(userId) {
  const res = await api(`/v1/users/${userId}`, null, 'DELETE')
  return res
}

export async function getAccounts(userId) {
  const res = await api(`/v1/users/${userId}/accounts`)
  return res
}

export async function addAccount(userId, accountNumber, accountPassword) {
  const req = { accountNumber, accountPassword: sha1(accountPassword.toUpperCase()) }
  const res = await api(`/v1/users/${userId}/accounts`, JSON.stringify(req), 'POST')
  return res
}

export async function deleteAccount(userId, accountNumber) {
  const res = await api(`/v1/users/${userId}/accounts/${accountNumber}`, null, 'DELETE')
  return res
}

export async function addVoucher(userId, voucherCode) {
  const req = { voucherCode: voucherCode.toUpperCase() }
  const res = await api(`/v1/users/${userId}/vouchers`, JSON.stringify(req), 'POST')
  return res
}

export async function logoutDevice() {
  const res = await api(`/v1/devices/logout`, null, 'DELETE')
  return res
}

// NOTE: This should be removed when all clients have switched to OTP
export async function verifyEmailToken(token) {
  return new Promise((resolve, reject) => {
    $.ajax(APP.getAjaxConfig({
      url: CONFIG.api_base_url + '/api/validate-token',
      method: 'POST',
      data: { token },
      success: resolve,
      error: function(jqXHR) {
        reject({ status: jqXHR.status })
      }
    }))
  })
}

export async function getUserVerificationToken(userId) {
  const headers = { 'User-Id': userId }
  const res = await api(`/v1/payments/user-verification-token`, null, 'GET', headers, true)
  if (res.headers.has('Payment-Token')) {
    return res.headers.get('Payment-Token').replace('Bearer ', '')
  }
  return null
}


/*
 *
 *  Bookings
 *
 */
export async function createBooking(values, userId) {
  const req = values?.serviceType === 'delivery' ? formatPackageBookingRequest(values, userId) : formatBookingRequest(values, userId)
  console.log(req)
  const res = await api('/v1/bookings', JSON.stringify(req), 'POST')
  return res
}

export async function prepareBooking(values, userId) {
  const req = values?.serviceType === 'delivery' ? formatPackageBookingRequest(values, userId) : formatBookingRequest(values, userId)
  const res = await api('/v1/bookings/prepare', JSON.stringify(req), 'POST')
  return res
}

export async function getBooking(bookingId) {
  const res = await api(`/v1/bookings/${bookingId}`)
  return res
}

export async function deleteBooking(bookingId, clientBookingToken) {
  // NOTE: Support anonymous bookings
  const headers = clientBookingToken ? { 'X-Order-Token': clientBookingToken } : null
  const res = await api(`/v1/bookings/${bookingId}`, null, 'DELETE', headers)
  return res
}

export async function sendConfirmationEmail(bookingId, email) {
  const req = { email }
  const res = await api(`/v1/bookings/${bookingId}/email-confirmation`, JSON.stringify(req), 'POST')
  return res
}

export async function getBookings(userId) {
  const res = await api(`/v1/bookings/users/${userId}?filter=active`)
  return res
}

export async function getHistoricBookings(userId) { const res = await api(`/v1/bookings/users/${userId}?filter=history&sort=DESC`)
  return res
}

export async function getStatus(bookings) {
  const req = formatStatusRequest(bookings)
  const res = await api(`/v1/bookings/status`, JSON.stringify(req), 'POST')
  return res
}

export async function getAnonymousBookings(bookings) {
  const req = bookings.map(booking => {
    return { bookingId: booking.orderId, clientBookingToken: booking.clientBookingToken }
  })
  const res = await api(`/v1/bookings/search`, JSON.stringify(req), 'POST')
  return res
}

export async function getPaymentToken(userId, bookingId) {
  const headers = { 'User-Id': userId }
  const req = { bookingId }
  const res = await api(`/v1/payments/payment-token`, JSON.stringify(req), 'POST', headers, true)
  if (res.headers.has('Payment-Token')) {
    return res.headers.get('Payment-Token').replace('Bearer ', '')
  }
  return null
}

export async function getPdfReceipt(userId, bookingId) {
  const headers = { 'User-Id': userId }
  const res = await api(`/v1/bookings/users/${userId}/${bookingId}/pdf-receipt`, null, 'GET', headers, true)
  return res
}

export async function getEditableFields(bookingId) {
  const res = await api(`/v1/bookings/${bookingId}/editable-fields`)
  return res
}

export async function getEditEstimate(bookingId, values) {
  const req = formatEditEstimateRequest(values)
  const res = await api(`/v1/bookings/${bookingId}/edit-estimate`, JSON.stringify(req), 'POST')
  return res
}

export async function confirmEdit(bookingId, editToken) {
  const req = { editToken }
  const res = await api(`/v1/bookings/${bookingId}/confirm-edit`, JSON.stringify(req), 'POST')
  return res
}


/*
 *
 *  Status messages
 *
 */
export async function sendEmailVerification(userId, email) {
  const res = await api(`/v1/users/${userId}/email-verification`, JSON.stringify({ email }), 'POST')
  return res
}

export async function getStatusMessages() {
  const date = new Date()
  const res = await api(`/v1/status-messages/search?dateTime=${date.toISOString()}`)
  return res
}
